// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, JsonPipe, NgFor, NgForOf, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  interval,
  map,
  startWith,
  takeUntil,
  tap,
} from 'rxjs';

import {
  JobStatus,
  JobTask,
} from 'src/app/api/remote-api/.portal.generated/models';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import { APICallGateComponent } from '../../../../shared/components/api-call-gate.component';
import { SharedModule } from '../../../../shared/shared.module';
import { SystemOverviewService } from '../../../services/systemOverview.service';
import { ApplicationInsightsService } from '../../../services/application-insights/application-insights-service/application-insights.service';


enum TaskState {
  none = 'None',
  running = 'Running',
  success = 'Success',
  failure = 'Failure',
}

export interface TaskAPI {
  trigger: SystemOverviewService['triggerSrsDataCollection'];
  getStatus: SystemOverviewService['getSrsDataCollectionStatus'];
}

@Component({
  selector: 'app-run-task-button',
  templateUrl: './run-task-button.component.html',
  styleUrls: ['./run-task-button.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],

  imports: [
    NgIf,
    NgForOf,
    NgFor,
    AsyncPipe,
    JsonPipe,
    TranslateModule,
    LocalizedDatePipe,
    SharedModule,
    APICallGateComponent,
  ],
})
export class RunTaskButtonComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) taskAPI!: TaskAPI;
  @Input({ required: true }) systemSerialNumber!: string;
  @Input({ required: true }) systemMaterialNumber!: number;
  @Input({ required: true }) systemSoftwareVersion!: string;
  @Input({ required: true }) systmModalityString!: string;
  @Input() fallbackPollIntervalSeconds = 3;

  defaultLabels = {
    lastExecutedLabel: 'Last successful job execution:',
    neverExecuted: 'never',
    button: '',
    triggering: 'Initializing..',
  };
  @Input() labels = this.defaultLabels;

  // observables need to be initialized late since we need the api
  // which is only passed as an @Input()
  jobStatus$?: Observable<DeepReadonly<JobStatus | undefined>>;
  tasks$?: Observable<DeepReadonly<JobTask[] | undefined>>;
  isInitialized$!: Observable<boolean>;
  isTriggering$!: Observable<boolean>;
  isRunning$!: Observable<boolean>;
  canTrigger$!: Observable<boolean>;
  lastUpdate$!: Observable<Moment | undefined>;
  lastTrigger$!: Observable<Moment | undefined>;
  nextPossibleTrigger$!: Observable<Moment>;
  secondsToNextTrigger$!: Observable<number>;
  
  wasTriggered$ = new BehaviorSubject(false);

  // right now we don't show individual task states
  // so in case any of them has an error. the first error will be provided by this observable
  overallErrorMessage$!: Observable<string | undefined>;

  jobIcon$!: Observable<string>;

  // latest and greatest job status (either from status endpoint or trigger endpoint)
  latestJob$!: BehaviorSubject<DeepReadonly<JobStatus> | undefined>;

  destroy$ = new Subject<void>();

  interval?: number;

  constructor(
    protected systemOverviewServce: SystemOverviewService,
    private applicationInsightsService: ApplicationInsightsService,
  ) {}

  ngOnDestroy(): void {
    if (this.interval) {
      window.clearTimeout(this.interval);
    }
    this.destroy$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.taskAPI) {
      this.taskAPI.getStatus.reset();
      this.taskAPI.trigger.reset();
      this.destroy$.next();
      this.latestJob$ = new BehaviorSubject<
        DeepReadonly<JobStatus> | undefined
      >(undefined);
      this.taskAPI.trigger.lastSuccessfulData$
        .pipe(
          takeUntil(this.destroy$),
          tap((d) => this.latestJob$.next(d))
        )
        .subscribe();
      this.taskAPI.getStatus.lastSuccessfulData$
        .pipe(
          takeUntil(this.destroy$),
          tap((d) => this.latestJob$.next(d))
        )
        .subscribe();

      this.jobStatus$ = this.latestJob$.pipe(
        map((last) => {
          if (last) {
            if (!this.interval && last && last.state === TaskState.running) {
              this.interval = window.setTimeout(() => {
                this.interval = undefined;
                this.fetchStatus();
              }, (last.pollIntervall || this.fallbackPollIntervalSeconds) * 1000);
            }
          }
          return last;
        })
      );
      this.tasks$ = this.jobStatus$.pipe(
        map((last) => last?.currentTasks || undefined)
      );
      this.isRunning$ = this.jobStatus$.pipe(
        map((s) => !!(s && s.state === TaskState.running))
      );
      this.isInitialized$ = this.jobStatus$.pipe(map((s) => !!s));
      this.isTriggering$ = this.taskAPI.trigger.loading$;
      this.lastUpdate$ = this.jobStatus$.pipe(
        map((s) => {
          return s?.lastCompletion ? moment(s?.lastCompletion) : undefined;
        })
      );
      this.lastTrigger$ = this.jobStatus$.pipe(
        map((s) =>
          s && s.state === TaskState.none ? moment(s.lastTrigger) : undefined
        )
      );
      this.nextPossibleTrigger$ = this.jobStatus$.pipe(
        map((s) => {
          let t = moment().subtract(1, 'second');
          if (!!s) {
            if (s?.lastCompletion) {
              t = moment(s.lastCompletion).add(s.coolDownInterval, 'seconds');
            }
            if (s?.lastTrigger) {
              t = moment(s.lastTrigger).add(s.coolDownInterval, 'seconds');
            }
          }

          return t;
        })
      );
      this.secondsToNextTrigger$ = combineLatest([
        this.nextPossibleTrigger$,
        interval(1000).pipe(map(() => moment())),
      ]).pipe(
        map(([nextPossible]) => {
          return nextPossible.diff(moment(), 'seconds');
        })
      );
      this.canTrigger$ = combineLatest([
        this.isTriggering$.pipe(startWith(false)),
        this.isRunning$.pipe(startWith(false)),
        this.isInitialized$.pipe(startWith(false)),
        this.secondsToNextTrigger$,
      ]).pipe(
        map(
          ([isTriggering, isRunning, isInitialized, secondsToNextTrigger]) => {
            return !!(
              !isRunning &&
              !isTriggering &&
              isInitialized &&
              secondsToNextTrigger < 0
            );
          }
        )
      );
      this.overallErrorMessage$ = this.jobStatus$.pipe(
        map((status) => {
          if (status?.state === 'Failure') {
            return (
              status.currentTasks?.find(
                (t) => t.taskState === TaskState.failure
              )?.label || undefined
            );
          }
          return undefined;
        })
      );
      this.jobIcon$ = combineLatest([
        this.wasTriggered$,
        this.isTriggering$,
        this.latestJob$,
      ]).pipe(
        map(([wasTriggered, triggering, job]) => {
          if (!wasTriggered || triggering) {
            return '';
          }
          switch (job?.state) {
            case TaskState.running:
              return '';
            case TaskState.success:
              return 'success';
            case TaskState.failure:
              return 'error';
            default:
              return '';
          }
        })
      );
    }
    this.fetchStatus();
  }

  triggerTask(): void {
    this.applicationInsightsService.trackComponentUsageEvent(
      'RequestNewDataClicked'
    );
    this.wasTriggered$.next(true);
    this.latestJob$.next(undefined);
    this.taskAPI.trigger.request({
      serialnumber: this.systemSerialNumber,
      materialnumber: this.systemMaterialNumber,
      softwareVersion: this.systemSoftwareVersion,
      modality: this.systmModalityString,
    });
  }

  fetchStatus(): void {
    this.taskAPI.getStatus.request({
      serialNumber: this.systemSerialNumber,
      materialNumber: this.systemMaterialNumber,
    });
  }
}
